import React, { useEffect, useState } from "react";
import { req } from "./api";
import { Field, Msg } from "./fields";
import Dashboard from "./Dashboard";
import { CasesAdmin, InsightsAdmin } from "./Content";
import EventsAdmin from "./EventsAdmin";
import MeetingsAdmin from "./MeetingsAdmin";
import Inbox from "./Inbox";
import { DocumentsAdmin, MediaAdmin } from "./Assets";
import ClientDocs from "./ClientDocs";
import DocStudio from "./DocStudio";
import { SettingsAdmin, UsersAdmin } from "./SettingsAdmin";

const TABS = [
  ["dashboard", "Dashboard"],
  ["enquiries", "Enquiries"],
  ["client-docs", "Client documents"],
  ["doc-studio", "AI document studio"],
  ["cases", "Case studies"],
  ["insights", "Insights"],
  ["events", "Events"],
  ["meetings", "Meetings"],
  ["documents", "Documents"],
  ["media", "Media"],
  ["settings", "Settings & figures"],
  ["users", "Users"],
];

export default function AdminApp() {
  const [user, setUser] = useState(null); // null=checking, false=logged out
  const [tab, setTab] = useState("dashboard");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Admin · Rivera Holdings";
    req("/auth/me").then(setUser).catch(() => setUser(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await req("/auth/login", { method: "POST", body: { email, password } });
      setUser(u);
    } catch (x) {
      setErr(x.message);
    }
    setBusy(false);
  };

  const logout = async () => {
    await req("/auth/logout", { method: "POST" }).catch(() => {});
    setUser(false);
  };

  if (user === null) return <div className="adm-login"><p className="muted">Loading…</p></div>;

  if (!user)
    return (
      <div className="adm-login" data-testid="admin-login">
        <div className="stack" style={{ gap: 6, marginBottom: 22 }}>
          <span className="eyebrow">Rivera Holdings</span>
          <h1 className="h3">Admin sign-in</h1>
        </div>
        <form className="stack" style={{ gap: 16 }} onSubmit={login}>
          <Field label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email" autoComplete="username" /></Field>
          <Field label="Password"><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password" autoComplete="current-password" /></Field>
          <Msg msg={err} />
          <button className="btn" type="submit" disabled={busy} data-testid="admin-login-submit">{busy ? "Signing in…" : "Sign in"}</button>
        </form>
        <p className="muted small" style={{ marginTop: 18 }}><a href="/">← Back to the website</a></p>
      </div>
    );

  const role = user.role;
  const visible = TABS.filter(([id]) =>
    id === "users" || id === "settings" ? role === "super_admin" : true);

  return (
    <div className="adm-shell" data-testid="admin-shell">
      <nav className="adm-side">
        <div className="adm-brand">
          <span className="brand-name" style={{ fontSize: 17, letterSpacing: ".14em", fontFamily: "var(--f-display)", color: "var(--ink)" }}>RIVERA</span>
          <div className="muted small">Admin panel</div>
        </div>
        {visible.map(([id, label]) => (
          <a key={id} href={`#${id}`} className={tab === id ? "on" : ""} data-testid={`admin-tab-${id}`}
            onClick={(e) => { e.preventDefault(); setTab(id); }}>{label}</a>
        ))}
      </nav>
      <main className="adm-main">
        <div className="adm-top">
          <div>
            <strong>{TABS.find(([id]) => id === tab)?.[1]}</strong>
            <div className="muted small">{user.name || user.email} · {role}</div>
          </div>
          <div className="row-actions">
            <a className="btn sm ghost" href="/" data-testid="admin-view-site">View site</a>
            <button className="btn sm ghost" onClick={logout} data-testid="admin-logout">Sign out</button>
          </div>
        </div>
        {tab === "dashboard" && <Dashboard go={setTab} />}
        {tab === "enquiries" && <Inbox />}
        {tab === "client-docs" && <ClientDocs />}
        {tab === "doc-studio" && <DocStudio />}
        {tab === "cases" && <CasesAdmin canEdit={["super_admin", "editor"].includes(role)} />}
        {tab === "insights" && <InsightsAdmin canEdit={["super_admin", "editor"].includes(role)} />}
        {tab === "events" && <EventsAdmin canEdit={["super_admin", "editor", "events"].includes(role)} />}
        {tab === "meetings" && <MeetingsAdmin canEdit={["super_admin", "events", "enquiries"].includes(role)} />}
        {tab === "documents" && <DocumentsAdmin canEdit={["super_admin", "editor"].includes(role)} />}
        {tab === "media" && <MediaAdmin canEdit={["super_admin", "editor", "events"].includes(role)} />}
        {tab === "settings" && <SettingsAdmin />}
        {tab === "users" && <UsersAdmin me={user} />}
      </main>
    </div>
  );
}
